/* Live2Kite: build a complete foil.
 *
 * Evan, 2026-09-19: "This widget would be nice to use for packages, complete foils. So
 * when someone picks the front wings first, then the only see the products that are
 * compatible."
 *
 * Four steps, each narrowed by the ones before: front wing, rear wing, fuselage, mast,
 * then the doodad and base plate an aluminium mast needs, chosen rather than asked.
 * Same engine, same rules, same catalogue as /pages/foil-fit and the product-page block,
 * so a setup this builds can never contradict what those two say.
 *
 * THE NARROWING FOLLOWS THE REAL DEPENDENCY, not the order of the page. A mast and a
 * rear hang off the FUSELAGE, not off the front wing (Evan), so the mast is filtered by
 * the chosen fuselage. The REAR is picked before any fuselage exists, so it is offered
 * against every fuselage the front wing can reach and the fuselage step then has to
 * satisfy both wings; that is what stops a rear choice leading to an empty step.
 *
 * ADDING TO CART: a POST to /cart/add.js with every chosen variant, then the cart page.
 * The /cart/<id>:1,<id>:1 permalink also works but jumps straight to checkout, which is
 * wrong for something somebody is still assembling.
 *
 * SIX PRODUCTS CANNOT BE ADDED BLIND: the four Red fuselages and the Red Windsurfing
 * carry doodad options, and the FATTY integrated e-foil mast has a cover option worth
 * $169. Adding "the cheapest variant" there would put the wrong part in the basket, so
 * those are added on their own product page and the builder says so.
 */
(function () {
  'use strict';

  // Evan, 2026-09-20: front wing, then REAR wing, then fuselage, then mast. The wings are
  // what a rider actually has an opinion about; the fuselage and mast are the plumbing
  // that has to agree with them. Choosing the rear before the fuselage means the rear is
  // narrowed by what the FRONT wing can reach rather than by a fuselage nobody has picked
  // yet, and then the fuselage has to satisfy BOTH wings.
  var STEPS = [
    { key: 'front', cat: 'front wing', label: 'Front wing', needs: null, hint: 'Start here. Everything else follows from it.' },
    { key: 'rear', cat: 'rear wing', label: 'Rear wing', needs: 'front', hint: 'Rear wings that can run with your front wing.' },
    { key: 'fuse', cat: 'fuselage', label: 'Fuselage', needs: 'rear', hint: 'Fuselages that take both of your wings.' },
    { key: 'mast', cat: 'mast', label: 'Mast', needs: 'fuse', hint: 'Masts that fit your fuselage.' }
  ];

  function el(t, c, x) { var e = document.createElement(t); if (c) e.className = c; if (x != null) e.textContent = x; return e; }
  function label(p) { return p.title.replace(/^AXIS Foils\s+/i, '').replace(/^AXIS\s+/i, ''); }
  function money(n) { return '$' + Number(n).toFixed(2).replace(/\.00$/, ''); }
  // Evan, 2026-09-20: "when you show price for each item, and there is a discount on it,
  // show the original price crossed out with the new price on it." compareAt is only set
  // in the data when it is ABOVE the price, so a stray compare-at can never show a
  // "saving" that costs the customer more.
  function priceNode(cls, p) {
    var w = el('span', cls);
    if (p.compareAt && Number(p.compareAt) > Number(p.price)) {
      w.appendChild(el('s', 'fb-was', money(p.compareAt)));
      w.appendChild(document.createTextNode(' '));
      w.appendChild(el('span', 'fb-now', money(p.price)));
    } else {
      w.appendChild(document.createTextNode(money(p.price)));
    }
    return w;
  }

  // Usage counts (Evan, 2026-10-28: "Yes" to counting who uses the builders): one anonymous call per event to
  // id.axisfoils.com, which keeps a daily count; no cookie, no id. Preview themes, the theme editor and automated
  // browsers do not report. Never in the way of the page.
  function usage(e) {
    try {
      var s = window.Shopify || {};
      if (navigator.webdriver || s.designMode || (s.theme && s.theme.role && s.theme.role !== 'main')) return;
      fetch('https://id.axisfoils.com/api/use?e=' + e, { mode: 'no-cors', keepalive: true, credentials: 'omit', referrerPolicy: 'strict-origin-when-cross-origin' }).catch(function () {});
    } catch (x) { /* nothing */ }
  }

  function init(root, data, rules, fit) {
    usage('build_open');
    var parts = data.parts;
    // Parts a complete setup needs that are not one of the four steps: the doodad and
    // the base plate an aluminium mast requires. Absent in older data, so default it.
    var extras = data.extras || {};
    var state = { front: null, fuse: null, mast: null, rear: null };

    function famRank(cat) {
      var order = (rules.familyOrder && rules.familyOrder[cat]) || [];
      var rank = {};
      order.forEach(function (f, i) { rank[f.toLowerCase()] = i; });
      return function (p) {
        var f = null;
        if (cat === 'fuselage') f = fit.fuselageFamily(rules, p.title);
        else if (cat === 'rear wing') f = fit.rearFamily(rules, p.title);
        else if (cat === 'mast') f = fit.mastFamily(rules, p.title);
        var name = (f && f.family) || p.family || '';
        var r = rank[name.toLowerCase()];
        return r === undefined ? order.length + 1 : r;
      };
    }

    /** Pricelist position, for fuselages (Evan, 2026-09-20). Listed parts first, in list order. */
    function plCompare(a, b) {
      var ra = a.plRank, rb = b.plRank;
      var ha = ra !== null && ra !== undefined, hb = rb !== null && rb !== undefined;
      if (ha && hb) return ra - rb;
      if (ha !== hb) return ha ? -1 : 1;
      return 0;
    }

    function sortFor(cat) {
      var rk = famRank(cat);
      return function (a, b) {
        if (cat === 'fuselage') { var pl = plCompare(a, b); if (pl) return pl; }
        var d = rk(a) - rk(b);
        if (d) return d;
        var sa = Number(a.span) || 0, sb = Number(b.span) || 0;
        if (sa !== sb) return sb - sa;
        var ca = Number(a.chord) || 0, cb = Number(b.chord) || 0;
        if (ca !== cb) return cb - ca;
        return label(a).localeCompare(label(b));
      };
    }

    /** The options for a step, already narrowed, each with its verdict. */
    function optionsFor(key) {
      // Only parts that can go in the cart (ported from the AXIS builder, 2026-10-19). The data carries Shopify's
      // availableForSale; offering a sold-out part let a rider build a setup the cart then refused whole, with
      // "The cart would not take that" (the Power Carbon 820 mast, sold out here, on 2026-10-19). Pre-order
      // variants sell on, so they stay available and stay offered.
      var all = function (c) { return parts.filter(function (p) { return p.category === c && p.available !== false; }); };
      if (key === 'front') {
        // Only wings the fit table can actually advise on. Offering a wing we cannot
        // place would build a setup we cannot stand behind.
        return all('front wing').filter(function (p) { return p.system; })
          .map(function (p) { return { p: p, v: { severity: 'ok', reasons: [] } }; })
          .sort(function (a, b) { return sortFor('front wing')(a.p, b.p); });
      }
      if (!state.front) return [];

      // Fuselages the chosen FRONT wing can go on. Everything downstream is judged
      // against this set, which is what keeps the rear step from offering a wing that
      // would leave no fuselage at all.
      var fusesForFront = all('fuselage').filter(function (f) { return fit.fuselageFits(rules, state.front, f).ok; });

      if (key === 'rear') {
        // A rear mounts to a FUSELAGE, and no fuselage is chosen yet, so offer the rears
        // that work with at least ONE fuselage this front wing can use. Offering every
        // rear would let somebody pick a Skinny Link behind a Black-system wing and hit
        // a dead end at the next step, with nothing to explain it.
        return all('rear wing').map(function (p) {
          var okOn = fusesForFront.filter(function (f) { return fit.rearFits(rules, f, p).ok; });
          return { p: p, v: okOn.length ? { ok: true, severity: 'ok', reasons: [] } : { ok: false } };
        }).filter(function (r) { return r.v.ok; })
          .sort(function (a, b) { return sortFor('rear wing')(a.p, b.p); });
      }
      if (!state.rear) return [];
      if (key === 'fuse') {
        // Must satisfy BOTH wings now.
        return fusesForFront.map(function (p) { return { p: p, v: fit.rearFits(rules, p, state.rear) }; })
          .filter(function (r) { return r.v.ok; })
          .sort(function (a, b) { return sortFor('fuselage')(a.p, b.p); });
      }
      if (!state.fuse) return [];
      return all('mast').map(function (p) { return { p: p, v: fit.mastFits(rules, state.fuse, p, state.front, null) }; })
        .filter(function (r) { return r.v.ok; })
        .sort(function (a, b) { return sortFor('mast')(a.p, b.p); });
    }

    // THE PICTURE PANEL (foil-build-viz.js; Evan, 2026-10-04: "each change they make, shows the new selection on
    // the product photo", "I want this feature to be amazing"): beside the steps on a computer, above them on a
    // phone. The builder keeps all the logic; the panel only draws what is chosen, and previews what is pointed at.
    var main = el('div', 'fb-main');
    var wrap = el('div', 'fb');
    main.appendChild(wrap);
    // The empty stage shows AXIS's own assembled foil (theme asset foil-build-empty.jpg), and each empty slot a real part
    // of that kind, the first in the range's own order (Evan, 2026-10-04: "more axis specific").
    var firstImage = function (cat) {
      var c = parts.filter(function (p) { return p.category === cat && p.image && p.available !== false; }).sort(sortFor(cat))[0];
      return c ? c.image : null;
    };
    var viz = window.AxisBuildViz ? window.AxisBuildViz.create({
      label: label, priceNode: priceNode,
      emptyImage: root.getAttribute('data-empty') || null,
      placeholders: { front: firstImage('front wing'), rear: firstImage('rear wing'), fuse: firstImage('fuselage'), mast: firstImage('mast') },
      onSlot: function (key) { var s = document.getElementById('fb-step-' + key); if (s) s.scrollIntoView({ behavior: 'smooth', block: 'start' }); },
      // The panel's button adds the setup itself when it can (Evan, 2026-10-19: "I built a foil, I clicked Review and add
      // to cart, I ended up with this cart"; it used to scroll to the summary and leave the adding to a second button).
      // When a part still needs its options chosen on its own page, it scrolls to the summary as before.
      reviewLabel: function () { return addNow ? 'Add setup to cart' : 'Review your setup'; },
      onReview: function (e) {
        if (addNow) { addNow(e && e.currentTarget); return; }
        var s = wrap.querySelector('.fb-sum'); if (s) s.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }) : null;
    var vizInfo = { total: null, was: null };
    var addNow = null;   // the summary's add-to-cart, for the panel's button; null when a part needs options first
    if (viz) { root.classList.add('fb-has-viz'); root.appendChild(viz.el); document.body.appendChild(viz.dock); }
    root.appendChild(main);

    function choose(key, p) {
      state[key] = p;
      // The steps are a chain now, each needing the one before: rear needs the front
      // wing, the fuselage needs BOTH wings, the mast needs the fuselage. So re-check
      // every LATER step, in order, and drop any choice that no longer fits, which in
      // turn re-checks the ones after it. Keeping a still-valid choice spares the rider
      // re-picking it; keeping an invalid one would put a part in the basket that does
      // not fit.
      var i = STEPS.map(function (x) { return x.key; }).indexOf(key);
      STEPS.slice(i + 1).forEach(function (st) {
        if (!state[st.key]) return;
        var still = optionsFor(st.key).some(function (r) { return r.p.handle === state[st.key].handle; });
        if (!still) state[st.key] = null;
      });
      render();
      // Bring the next step into view: a choice closes a long list above it, and on a phone the page used to land in the
      // middle of the next list with its heading off screen (2026-10-04). After the last part, the setup summary.
      var nxt = STEPS.filter(function (st) { return !state[st.key]; })[0];
      var target = nxt ? document.getElementById('fb-step-' + nxt.key) : wrap.querySelector('.fb-sum');
      if (target) {
        var top = target.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.6) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    // Clearing a step clears everything after it: without the earlier choice there is
    // nothing to judge the later ones against, and the summary would otherwise list a
    // fuselage with no rear wing to explain it.
    function clearFrom(key) {
      var i = STEPS.map(function (x) { return x.key; }).indexOf(key);
      STEPS.slice(i).forEach(function (st) { state[st.key] = null; });
      render();
    }

    function card(step, r) {
      var b = el('button', 'fb-card');
      b.type = 'button';
      if (r.p.image) { var im = el('img', 'fb-img'); im.src = r.p.image; im.alt = ''; im.loading = 'lazy'; b.appendChild(im); }
      b.appendChild(el('span', 'fb-card-name', label(r.p)));
      if (r.p.price) b.appendChild(priceNode('fb-card-price', r.p));
      if (r.v.severity === 'info' || r.v.severity === 'warn') {
        b.appendChild(el('span', 'fb-card-note ' + (r.v.severity === 'warn' ? 'fb-warn' : 'fb-info'), r.v.reasons[0] || ''));
      }
      if (r.p.stock === 'incoming') b.appendChild(el('span', 'fb-card-note', 'On order'));
      b.addEventListener('click', function () { choose(step.key, r.p); });
      if (viz) viz.attach(b, r.p, step.key);
      return b;
    }

    function render() {
      wrap.innerHTML = '';
      STEPS.forEach(function (step, i) {
        var chosen = state[step.key];
        var prior = !step.needs || !!state[step.needs];
        var sec = el('section', 'fb-step' + (chosen ? ' fb-done' : '') + (prior ? '' : ' fb-locked'));
        sec.id = 'fb-step-' + step.key;
        var h = el('h3', 'fb-step-head');
        h.appendChild(el('span', 'fb-num', String(i + 1)));
        h.appendChild(el('span', 'fb-step-label', step.label));
        sec.appendChild(h);
        var DD = window.AxisBuildViz.dropdown;

        if (!prior) {
          var needed = STEPS.filter(function (x) { return x.key === step.needs; })[0];
          sec.appendChild(DD({ key: step.key, disabledText: 'Choose your ' + needed.label.toLowerCase() + ' first' }));
          wrap.appendChild(sec);
          return;
        }

        var opts = optionsFor(step.key);
        if (!opts.length) {
          sec.appendChild(el('p', 'fb-hint', 'Nothing in the shop fits that combination. Call us on 415.924.9463.'));
          wrap.appendChild(sec);
          return;
        }
        // ONE PULL-DOWN PER STEP (Evan, 2026-10-04: "Each product category should be a pull down menu. Otherwise the endless
        // options are overwhelming"). It replaces the grid of picture cards. Grouped by family in the rules' familyOrder
        // (the options arrive sorted that way); fuselages by LINE (Black Advance+, Advance 20, Advance+ Fatty, Ti Link,
        // Red ...), since their family names carry the length and made one group per length.
        function groupOf(p) {
          var f = p.family || 'Other';
          if (p.category !== 'fuselage') return f;
          f = f.replace(/\b(standard|short|ultrashort|ultra short|crazyshort|sillyshort|psychoshort|windsurf(ing)?)\b/gi, '').replace(/\s+/g, ' ').trim();
          if (/fatty/i.test(p.title) && !/fatty/i.test(f)) f += ' Fatty';
          return f || 'Other';
        }
        var groups = [], byFam = {};
        opts.forEach(function (r) {
          var fam = groupOf(r.p);
          if (!byFam[fam]) { byFam[fam] = { name: fam, items: [] }; groups.push(byFam[fam]); }
          var notes = [];
          if (r.v.severity === 'info' || r.v.severity === 'warn') notes.push(r.v.reasons[0] || '');
          if (r.p.stock === 'incoming') notes.push('On order');
          byFam[fam].items.push({ p: r.p, note: notes.join(' '), warn: r.v.severity === 'warn' });
        });
        var nextKey = (STEPS.filter(function (st) { return !state[st.key]; })[0] || {}).key;
        sec.appendChild(DD({
          key: step.key,
          placeholder: 'Choose your ' + step.label.toLowerCase(),
          sub: opts.length + (opts.length === 1 ? ' fits' : ' fit') + (step.key === 'front' ? ' the range' : ' your setup'),
          chosen: chosen, groups: groups, next: step.key === nextKey,
          label: label, priceNode: priceNode, viz: viz,
          onChoose: function (p) { choose(step.key, p); }
        }));
        wrap.appendChild(sec);
      });

      wrap.appendChild(summary());
      if (viz) viz.update({ state: state, total: vizInfo.total, was: vizInfo.was, extras: requiredExtras() });
    }

    /**
     * Parts a setup NEEDS that are not one of the four steps.
     *
     * A 19mm ALUMINIUM mast bolts to the fuselage through a doodad and brings no base
     * plate; a carbon mast ships WITH its base plate and needs neither. Before this, the
     * builder called a four-item basket a "Complete setup" that could not actually be
     * assembled: the doodad warning appears on the mast card and then vanishes the moment
     * the step collapses, so by the time somebody reached the total it was gone.
     */
    function requiredExtras() {
      var out = [];
      var mast = state.mast, fuse = state.fuse;
      if (!mast || !fuse) return out;
      var mastName = label(mast);
      var isAlu = /alumini?um/i.test(String(mast.construction || '') + ' ' + mastName);
      // Carbon masts arrive as "Mast & Base Plate" and bolt straight on. Nothing to add,
      // and nothing to ask.
      if (!isAlu) return out;

      var fuseName = String(fuse.family || '') + ' ' + label(fuse);
      var isLink = /ti[\s-]?link/i.test(fuseName);
      var isFatty = /fatty/i.test(fuseName);
      // The generation is read off the MAST: the black system has its own doodad and
      // base plate at different prices.
      var isBlackGen = /\bBLACK\b/i.test(mastName);

      // THE DOODAD IS DECIDED BY THE FUSELAGE, so it is chosen here rather than asked.
      // Evan: "you don't want to keep on asking people what else they want, you want to
      // tell them, since you chose a fuselage already".
      if (!isLink) {
        var dd = isFatty ? extras.doodadFatty
          : (isBlackGen ? (extras.doodadBlack || extras.doodad) : (extras.doodad || extras.doodadBlack));
        if (dd) {
          out.push({
            part: dd,
            why: isFatty
              ? 'Your FATTY fuselage takes the FATTY doodad to accept a 19mm mast.'
              : 'Your ' + (isBlackGen ? 'black ' : '') + 'aluminium mast bolts to this fuselage through the doodad.'
          });
        }
      }
      // The base plate joins the mast to the BOARD, so it is needed whatever the
      // fuselage; only the generation changes.
      var bp = isBlackGen ? (extras.basePlateBlack || extras.basePlate) : (extras.basePlate || extras.basePlateBlack);
      if (bp) out.push({ part: bp, why: 'An aluminium mast does not come with a base plate; a carbon mast does.' });
      return out;
    }

    function summary() {
      var box = el('section', 'fb-sum');
      box.appendChild(el('h3', null, 'Your setup'));
      var picked = STEPS.map(function (s) { return { s: s, p: state[s.key] }; }).filter(function (x) { return x.p; });
      if (!picked.length) {
        vizInfo = { total: null, was: null };
        addNow = null;
        box.appendChild(el('p', 'fb-hint', 'Pick a front wing to begin.'));
        return box;
      }
      var total = 0, was = 0, ul = el('ul', 'fb-sum-list');
      picked.forEach(function (x) {
        var li = el('li');
        li.appendChild(el('span', 'fb-sum-cat', x.s.label));
        var a = el('a', 'fb-sum-name', label(x.p));
        a.href = '/products/' + x.p.handle;
        li.appendChild(a);
        if (x.p.price) { li.appendChild(priceNode('fb-sum-price', x.p)); total += Number(x.p.price); was += Number(x.p.compareAt || x.p.price); }
        ul.appendChild(li);
      });
      var needed = requiredExtras();
      needed.forEach(function (n) {
        var li = el('li', 'fb-sum-extra');
        li.appendChild(el('span', 'fb-sum-cat', 'Also added'));
        var a = el('a', 'fb-sum-name', n.part.title.replace(/^AXIS Foils /, ''));
        a.href = '/products/' + n.part.handle;
        li.appendChild(a);
        if (n.part.price) { li.appendChild(priceNode('fb-sum-price', n.part)); total += Number(n.part.price); was += Number(n.part.compareAt || n.part.price); }
        li.appendChild(el('span', 'fb-sum-why', n.why));
        ul.appendChild(li);
      });
      box.appendChild(ul);

      var t = el('p', 'fb-total');
      t.appendChild(el('span', null, picked.length === STEPS.length ? 'Complete setup' : picked.length + ' of 4 chosen'));
      // The whole setup's saving, the same way: original total crossed out beside the
      // real one, only when something in it is actually discounted.
      t.appendChild(priceNode('fb-total-n', { price: Math.round(total * 100) / 100, compareAt: Math.round(was * 100) / 100 }));
      box.appendChild(t);
      vizInfo = { total: Math.round(total * 100) / 100, was: Math.round(was * 100) / 100 };

      var all = picked.concat(needed.map(function (n) { return { s: { label: 'Also added' }, p: n.part }; }));
      var addable = all.filter(function (x) { return x.p.variantId && x.p.variantCount === 1; });
      var manual = all.filter(function (x) { return x.p.variantCount > 1; });

      var btn = el('button', 'fb-add', 'Add ' + addable.length + (addable.length === 1 ? ' item' : ' items') + ' to cart');
      btn.type = 'button';
      btn.disabled = !addable.length;
      // One add for both buttons: this one and the picture panel's "Add setup to cart" (passed in as other).
      var add = function (other) {
        btn.disabled = true;
        btn.textContent = 'Adding...';
        if (other) { other.disabled = true; other.textContent = 'Adding...'; }
        Promise.resolve().then(function () {
          return fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: addable.map(function (x) { return { id: Number(x.p.variantId), quantity: 1, properties: { '_Added from': 'setup builder' } }; }) })
          });
        }).then(function (r) {
          if (!r.ok) throw new Error('cart refused');
          usage('build_add');
          window.location.href = '/cart';
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = 'Add ' + addable.length + ' to cart';
          if (other) { other.disabled = false; other.textContent = 'Add setup to cart'; }
          box.appendChild(el('p', 'fb-warn', 'The cart would not take that just now. Call us on 415.924.9463 and we will put the setup together for you.'));
          box.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      };
      btn.addEventListener('click', function () { add(); });
      addNow = (addable.length && !manual.length) ? add : null;
      box.appendChild(btn);

      if (manual.length) {
        // These carry options (a doodad size, a mast cover at +169). Adding a guessed
        // variant would put the wrong part in somebody's basket.
        var m = el('p', 'fb-manual');
        m.appendChild(document.createTextNode('Choose your options for '));
        manual.forEach(function (x, i) {
          var a = el('a', null, label(x.p));
          a.href = '/products/' + x.p.handle;
          m.appendChild(a);
          if (i < manual.length - 1) m.appendChild(document.createTextNode(', '));
        });
        m.appendChild(document.createTextNode(' on its own page, then add it.'));
        box.appendChild(m);
      }
      return box;
    }

    render();
  }

  function boot() {
    var root = document.getElementById('FoilBuild');
    if (!root) return;
    Promise.all([
      fetch(root.getAttribute('data-parts')).then(function (r) { return r.json(); }),
      fetch(root.getAttribute('data-rules')).then(function (r) { return r.json(); })
    ]).then(function (res) {
      if (!window.AxisFit) throw new Error('fit engine did not load');
      if (!window.AxisBuildViz) throw new Error('builder panel (foil-build-viz.js) did not load');
      root.innerHTML = '';
      init(root, res[0], res[1], window.AxisFit);
    }).catch(function (e) {
      root.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'fb-hint';
      p.textContent = 'The setup builder could not load just now. Call us on 415.924.9463 and we will put a setup together with you.';
      root.appendChild(p);
      if (window.console) console.error('foil build:', e);
    });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { };
  else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
